"use client"

import { createContext, use, useCallback, useEffect, useState } from "react"

import {
  applyResolvedTheme,
  DARK_COLOR_SCHEME_QUERY,
  DEFAULT_THEME_PREFERENCE,
  isThemePreference,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type ThemePreference,
} from "@/lib/theme"

interface ThemeContextValue {
  /** The stored preference, including `system`. */
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
  /** Whether this route supports themes. False hides both pickers. */
  enabled: boolean
  /**
   * The theme actually on `<html>`, with `system` resolved and route availability
   * applied. For surfaces that cannot read CSS tokens — a canvas, or a library
   * that themes itself off its own class, as React Flow does with
   * `.react-flow.dark`. Style with tokens everywhere else.
   */
  resolvedTheme: ResolvedTheme
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/**
 * Owns the theme preference and keeps the `.dark` class on `<html>` in sync.
 *
 * The first paint is handled by `THEME_INIT_SCRIPT` in the root layout, so this
 * provider starts from whatever that script already applied — server rendering
 * has no access to the preference and must not fight it.
 */
export function ThemeProvider({
  children,
  enabled,
}: {
  children: React.ReactNode
  enabled: boolean
}) {
  const [preference, setPreference] = useState<ThemePreference>(() =>
    globalThis.window === undefined || !enabled
      ? DEFAULT_THEME_PREFERENCE
      : readStoredPreference()
  )

  // Match the bootstrap script on the client so consumers such as React Flow
  // receive the resolved system theme on their first render. The server falls
  // back to false because it cannot access media queries.
  const [prefersDark, setPrefersDark] = useState<boolean>(() =>
    globalThis.window === undefined || !enabled
      ? false
      : globalThis.window.matchMedia(DARK_COLOR_SCHEME_QUERY).matches
  )

  useEffect(() => {
    // Signed-out, bypass and fallback routes retain their light appearance even
    // when this origin has a stored preference from an authenticated app page.
    if (!enabled) return

    const media = window.matchMedia(DARK_COLOR_SCHEME_QUERY)
    const sync = () => {
      setPrefersDark(media.matches)
      applyResolvedTheme(resolveTheme(preference, media.matches))
    }

    sync()
    media.addEventListener("change", sync)
    return () => {
      media.removeEventListener("change", sync)
    }
  }, [preference, enabled])

  // Keep other tabs of the same app in step with the preference.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return
      setPreference(readStoredPreference())
    }

    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener("storage", onStorage)
    }
  }, [])

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(next)
    writeStoredPreference(next)
  }, [])

  // Mirrors what the effect above puts on `<html>`, including the route guard,
  // so a consumer and the class can never disagree.
  const resolvedTheme: ResolvedTheme = enabled
    ? resolveTheme(preference, prefersDark)
    : "light"

  return (
    <ThemeContext
      value={{ theme: preference, setTheme, enabled, resolvedTheme }}
    >
      {children}
    </ThemeContext>
  )
}

export function useTheme(): ThemeContextValue {
  const context = use(ThemeContext)

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}

/**
 * Read and write the raw preference string directly rather than through
 * `useLocalStorage`: that hook JSON-encodes its values, and `THEME_INIT_SCRIPT`
 * has to read this key before hydration with no deserializer. The key is
 * deliberately not tenant-scoped — the theme is a per-device UI preference that
 * should survive switching workspaces. Routes without the authenticated theme
 * bootstrap marker retain their light appearance.
 *
 * Storage access itself throws when cookies are blocked (Safari private
 * browsing, embedded MS Teams tabs), hence the guards.
 */
function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE
  } catch {
    return DEFAULT_THEME_PREFERENCE
  }
}

function writeStoredPreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Without storage the choice still applies, it just will not survive a reload.
    return
  }
}
